// src/config/apiConfig.js
// const BASE_URL = 'https://api.lodgeic.io/v1';
// const BASE_URL = 'http://localhost:8080/api';

let determinedBaseUrl;

if (process.env.REACT_APP_ENV === 'fastify-prod') {
  determinedBaseUrl = process.env.REACT_APP_BASE_URL || 'https://lodgeic-api-h2fkgkbhcjf3hxe9.eastus-01.azurewebsites.net';
} else if (process.env.REACT_APP_ENV === 'production') {
  determinedBaseUrl = 'https://api.lodgeic.io/v1';
} else if (process.env.REACT_APP_ENV === 'demo') {
  // Demo talks straight to the `demo` deployment slot of the lodgeic-api App
  // Service (fastify) — the demo APIM (lodgeic-demo-apim) is being retired.
  // Requires the slot to run a fastify build with full route coverage (deploys
  // from the `demo` branch via demo_lodgeic-api(demo).yml).
  determinedBaseUrl = 'https://lodgeic-api-demo-asb7cqh6h6bzggbb.eastus-01.azurewebsites.net/api';
} else if (process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'development') {
  // Local dev talks to the fastify host (port 8080) — the Functions host on
  // 7071 is legacy. .env.development sets REACT_APP_BASE_URL; honor an
  // explicit override (start:devServer points at a remote host).
  determinedBaseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080/api';
} else {
  determinedBaseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080/api';
}

const BASE_URL = determinedBaseUrl;

// Fastify handles AI endpoints — routed through APIM /ai suffix in prod/demo, localhost in dev
let determinedFastifyUrl;
if (process.env.REACT_APP_ENV === 'production') {
  determinedFastifyUrl = 'https://lodgeic-api-h2fkgkbhcjf3hxe9.eastus-01.azurewebsites.net/api';
} else if (process.env.REACT_APP_ENV === 'demo') {
  determinedFastifyUrl = 'https://lodgeic-api-demo-asb7cqh6h6bzggbb.eastus-01.azurewebsites.net/api';
} else {
  determinedFastifyUrl = process.env.REACT_APP_FASTIFY_URL || 'http://localhost:8080/api';
}
const FASTIFY_BASE_URL = determinedFastifyUrl;

const API_ENDPOINTS = {
    GET_ORG_CONFIG: `${BASE_URL}/orgConfig`,
    DASHBOARD_DATA: `${BASE_URL}/dashboardData`,
    GET_CHARTOFACCOUNTS: `${BASE_URL}/chartOfAccounts`,
    FINANCIAL_BUDGET: `${BASE_URL}/financialBudget`,
    LABOR_BUDGET_MONTHS: `${FASTIFY_BASE_URL}/laborBudget/months`,
    GET_PDF_PROXY: `${BASE_URL}/pbiPdfProxy`,
    GET_USER_SETTINGS: (email) => `${BASE_URL}/userSettings?id=${email}`,
    USER_SETTINGS: `${BASE_URL}/userSettings`,
    // Consolidated initialization data endpoint (BFF pattern). Kept on BASE_URL
    // deliberately: api.lodgeic.io is CNAMEd onto the fastify App Service, so
    // this hits azfApi/fastify/routes/org/initData.js via the /v1 rewrite alias.
    // Do NOT point this at FASTIFY_BASE_URL — the domain must stay the single
    // control point so a DNS rollback moves ALL traffic.
    INIT_DATA: `${BASE_URL}/initData`,
    GET_STREAM_CHAT_TOKEN: `${BASE_URL}/streamChatToken`,
    UPSERT_STREAM_CHAT_USERS: `${BASE_URL}/streamChatUpsertUsers`,
    LOCATIONS: `${BASE_URL}/locations`,
    GET_LOCATIONS_BY_ORG: (orgId) => `${BASE_URL}/locations?orgId=${orgId}`,
    DEPARTMENTS: `${BASE_URL}/departments`,
    GET_DEPARTMENTS_BY_ORG: (orgId) => `${BASE_URL}/departments?orgId=${orgId}`,
    ORGANIZATIONS: `${BASE_URL}/organizations`,
    GET_ORGANIZATION_BY_CODE: (code) => `${BASE_URL}/organizations?code=${code}`,
    GET_ORGANIZATION_BY_ID: (id) => `${BASE_URL}/organizations?id=${id}`,
    DASHBOARD_FACT_REFRESH: `${BASE_URL}/dashboardFactRefresh`,
    USER_ACTIVITY_LOG: `${BASE_URL}/userActivityLog`,
    GET_REVENUE_TYPES: `${BASE_URL}/revenueTypes`,
    GET_REVENUE_TYPES_BY_ORG: (orgId, locationId, includeDeleted = false) => {
      let url = `${BASE_URL}/revenueTypes?orgId=${orgId}`;
      if (locationId) url += `&locationId=${locationId}`;
      if (includeDeleted) url += `&includeDeleted=true`;
      return url;
    },
    GET_JOURNAL_ENTRY_TYPES: `${BASE_URL}/journalEntryTypes`,
    GET_JOURNAL_ENTRY_TYPES_BY_ORG: (orgId, locationId, includeDeleted = false) => {
      let url = `${BASE_URL}/journalEntryTypes?orgId=${orgId}`;
      if (locationId) url += `&locationId=${locationId}`;
      if (includeDeleted) url += `&includeDeleted=true`;
      return url;
    },
    GET_HOTEL_MARKET_REVENUE_TYPES: `${BASE_URL}/hotelMarketRevenueTypes`,
    GET_HOTEL_MARKET_REVENUE_TYPES_BY_ORG: (orgId, locationId) => locationId ?
      `${BASE_URL}/hotelMarketRevenueTypes?orgId=${orgId}&locationId=${locationId}` :
      `${BASE_URL}/hotelMarketRevenueTypes?orgId=${orgId}`,

    // Report Subscription endpoints
    REPORT_SUBSCRIPTIONS: `${BASE_URL}/reportSubscription`,
    TEST_REPORT_SUBSCRIPTION: (id) => `${BASE_URL}/reportSubscription/test?id=${id}`,
    NATIVE_REPORT_SUBSCRIPTION_TEST: (id) => `${FASTIFY_BASE_URL}/reportSubscriptionTest?id=${id}&native=true`,

    // Financial Forecast endpoint (legacy SQL)
    FINANCIAL_FORECAST: `${BASE_URL}/financialForecast`,

    // Forecast Accounts endpoint (legacy SQL)
    FORECAST_ACCOUNTS: `${BASE_URL}/forecastAccounts`,

    // Forecast Detail endpoint (SQL view)
    FORECAST_DETAIL: `${BASE_URL}/forecastDetail`,

    // Forecast Detail Daily endpoint (SQL view with daily granularity)
    FORECAST_DETAIL_DAILY: `${BASE_URL}/forecastDetailDaily`,

    // Unified Forecast Cosmos DB endpoint (NEW - handles both forecast accounts and forecast data)
    FORECAST_COSMOS: `${BASE_URL}/forecastCosmos`,

    ROOM_STAT_ACCOUNT_MAPPINGS: `${BASE_URL}/roomStatAccountMappings`,

    // Forecast V3 endpoints (version-based forecasting)
    FORECAST_V3_VERSIONS: `${BASE_URL}/forecast/v3/versions`,
    // AI build/rooms-forecast actions live on the fastify server in every env
    // (the Functions twin predates them and rejects the action posts)
    FORECAST_V3_VERSIONS_AI: `${FASTIFY_BASE_URL}/forecast/v3/versions`,
    // Org/location export layouts (org-config budgetExportFormats) — fastify-only
    FORECAST_V3_EXPORT_FORMATS: `${FASTIFY_BASE_URL}/forecast/v3/exportFormats`,
    // Full-year forecast snapshots ("as of" views) — fastify-only
    FORECAST_V3_SNAPSHOTS: `${FASTIFY_BASE_URL}/forecast/v3/snapshots`,
    FORECAST_V3_COMPARE: `${BASE_URL}/forecast/v3/compare`,
    FORECAST_V3_CALCULATE: `${BASE_URL}/forecast/v3/calculate`,
    FORECAST_V3_DRIVER_RECOMMENDATION: `${BASE_URL}/forecast/v3/driverRecommendation`, // AI driver pick (OpenRouter, experimental)
    FORECAST_V3_EVENTS: `${BASE_URL}/forecast/v3/events`,
    FORECAST_V3_PNL: `${BASE_URL}/forecastV3PnL`, // Lightweight P&L data (no calculations)
    FORECAST_RISK: `${BASE_URL}/forecastRisk`, // Historical context for risk flagging

    // Transactions endpoint (GL detail drill-down)
    TRANSACTIONS: `${BASE_URL}/transactions`,

    // Location Comp Set endpoints
    LOCATION_COMP_SETS: `${BASE_URL}/locationCompSet`,
    GET_LOCATION_COMP_SETS_BY_ORG: (orgId) => `${BASE_URL}/locationCompSet?orgId=${orgId}`,
    GET_LOCATION_COMP_SETS_BY_ORG_LOCATION: (orgId, locationId) => `${BASE_URL}/locationCompSet?orgId=${orgId}&locationId=${locationId}`,
    GET_LOCATION_COMP_SET_BY_ID: (id) => `${BASE_URL}/locationCompSet?id=${id}`,
    // Labor Budget endpoint
    LABOR_BUDGET: `${BASE_URL}/laborBudget`,
    // Positions endpoint
    POSITIONS: `${BASE_URL}/positions`,
    // File Upload ETL endpoint
    FILE_UPLOAD_ETL: `${BASE_URL}/fileUploadEtl`,
    // File Conversion endpoint
    FILE_CONVERSION: `${BASE_URL}/fileConversion`,
    // HIA accounting GL import endpoint
    HIA_GL_IMPORT: `${BASE_URL}/hiaGlImport`,
    // Admin Password Reset endpoint
    ADMIN_PASSWORD_RESET: `${BASE_URL}/userPasswordReset`,
    // Revenue Export endpoint
    REVENUE_EXPORT: `${BASE_URL}/revenueExport`,
    // Imports endpoint
    IMPORTS: `${BASE_URL}/imports`,
    // Type Classifications endpoint (global lookup table)
    TYPE_CLASSIFICATIONS: `${BASE_URL}/typeClassifications`,
    // Revenue Basis Groups endpoint (position revenue comparison sets)
    REVENUE_BASIS_GROUPS: `${BASE_URL}/revenueBasisGroups`,
    // Department Chart of Account Mappings endpoint
    DEPARTMENT_COA_MAPPINGS: `${BASE_URL}/departmentChartOfAccountMappings`,
    // Labor Chart of Account Mappings endpoint (maps labor departments to GL accounts)
    LABOR_COA_MAPPINGS: `${BASE_URL}/laborChartOfAccountMappings`,
    // Labor position payroll analytics (per-employee stats for the labor driver modal)
    LABOR_POSITION_STATS: `${BASE_URL}/laborPositionStats`,
    LABOR_POSITION_MONTHLY: `${BASE_URL}/laborPositionMonthly`,
    // Labor Detail endpoint (labor data by GL account, position, employee)
    LABOR_DETAIL: `${BASE_URL}/laborDetail`,
    // AI Position to GL Account Mapping endpoint
    AI_POSITION_GL_MAPPING: `${FASTIFY_BASE_URL}/aiPositionGlMapping`,
    // AI Revenue Type to COA Mapping endpoint
    AI_REVENUE_COA_MAPPING: `${BASE_URL}/aiRevenueCoaMapping`,
    // Night Audit Risk Alerts
    NIGHT_AUDIT_ALERTS: `${BASE_URL}/nightAuditAlerts`,
    // Night Audit Admin Config (frequency settings per location)
    NIGHT_AUDIT_ADMIN_CONFIG: `${BASE_URL}/nightAuditAdminConfig`,
    // AI Journal Entry Type to COA Mapping endpoint
    AI_JOURNAL_COA_MAPPING: `${BASE_URL}/aiJournalCoaMapping`,
    // Journal Export module is served by the fastify host (Functions handlers
    // are reused there via the functionAdapter shim — one implementation).
    JOURNAL_EXPORT: `${FASTIFY_BASE_URL}/journalExport`,
    JOURNAL_ADJUSTMENTS: `${FASTIFY_BASE_URL}/journalAdjustments`,
    JOURNAL_CLOSEOUTS: `${FASTIFY_BASE_URL}/journalCloseouts`,
    JOURNAL_ACCOUNTING_EXPORT: `${FASTIFY_BASE_URL}/journalAccountingExport`,
    JOURNAL_SOURCE_RULES: `${FASTIFY_BASE_URL}/journalSourceRules`,
    ACCOUNTING_SYNC: `${FASTIFY_BASE_URL}/accountingSync`,
    HELP_CENTER: `${FASTIFY_BASE_URL}/helpCenter`,
    STAT_JOURNAL_MAPPINGS: `${FASTIFY_BASE_URL}/statJournalMappings`,
    // Report Templates (Financial Analyst)
    REPORT_TEMPLATES: `${BASE_URL}/reportTemplates`,
    REPORT_TEMPLATE_DEPARTMENTS: `${BASE_URL}/reportTemplateDepartments`,
    GENERATE_FINANCIAL_REPORT: `${BASE_URL}/generateFinancialReport`
};

export default API_ENDPOINTS;
export { BASE_URL, FASTIFY_BASE_URL };
